// useDashboard fetches /api/dashboard on mount and exposes the aggregate
// stats + activity feed for the Home page. Keep this hook intentionally
// thin so the page does not flicker between empty and loaded; loading
// state surfaces as `null` and the page renders skeleton placeholders.

import { useEffect, useState } from 'react';
import { useAuthedFetch } from './useAuthedFetch';

export type ActivityKind = 'medication_added' | 'flag_run' | 'chat_started' | 'consult_requested';

export interface DashboardActivity {
  kind: ActivityKind;
  occurredAt: string;
  detail: string | null;
}

export interface Dashboard {
  medicationCount: number;
  flagTotal: number;
  flagCounts: { critical: number; major: number; moderate: number; minor: number };
  conversationCount: number;
  consultCount: number;
  safetyScore: number;
  activity: DashboardActivity[];
}

export function useDashboard(): { dashboard: Dashboard | null; loading: boolean } {
  const authedFetch = useAuthedFetch();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/dashboard');
        if (!res.ok) return;
        const json = (await res.json()) as { ok: boolean; data?: Dashboard };
        if (!cancelled && json.ok && json.data) setDashboard(json.data);
      } catch {
        // Soft fail; the home page renders friendly placeholders.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  return { dashboard, loading };
}

/** Format an ISO timestamp as "2 hours ago", "yesterday", "3 days ago". */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diffSec < 86400) {
    const h = Math.floor(diffSec / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.floor(diffSec / 86400);
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  const months = Math.floor(d / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(d / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}
